import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Plus, Trash2, Edit, X, LogOut, Store, Calendar, Users } from "lucide-react";
import { toast } from "sonner";

import Header from "@/components/Header";
import { adminApi, adminLogin, fetchStores, fetchEvents } from "@/lib/api";

const TOKEN_KEY = "zl_admin_token";

const emptyStore = {
  name: "",
  address: "",
  phone: "",
  city: "",
  latitude: "",
  longitude: "",
  image_url: "",
  hours: "Mon - Sat: 10:00 AM - 9:00 PM",
};

const emptyEvent = {
  store_id: "",
  title: "",
  description: "",
  event_date: "",
  image_url: "",
  capacity: 50,
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [tab, setTab] = useState("stores");
  const [stores, setStores] = useState([]);
  const [events, setEvents] = useState([]);
  const [editingStore, setEditingStore] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [rsvpsView, setRsvpsView] = useState(null);

  const authed = !!token;
  const apiClient = authed ? adminApi(token) : null;

  const loadData = async () => {
    try {
      const [s, e] = await Promise.all([fetchStores(), fetchEvents()]);
      setStores(s);
      setEvents(e);
    } catch {
      toast.error("Could not load data");
    }
  };

  useEffect(() => {
    if (authed) loadData();
  }, [authed]);

  const handleLogin = async (e) => {
    e.preventDefault();
    const pwd = password.trim();
    if (!pwd) return toast.error("Please enter the password");
    setLoading(true);
    try {
      const res = await adminLogin(pwd);
      setToken(res.token);
      localStorage.setItem(TOKEN_KEY, res.token);
      toast.success("Welcome, admin");
    } catch {
      toast.error("Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setToken("");
    localStorage.removeItem(TOKEN_KEY);
    toast.success("Signed out");
  };

  const saveStore = async () => {
    const s = editingStore;
    if (!s.name || !s.address || !s.phone || !s.city || s.latitude === "" || s.longitude === "") {
      return toast.error("Fill all required fields");
    }
    const payload = { ...s, latitude: parseFloat(s.latitude), longitude: parseFloat(s.longitude) };
    try {
      if (s.id) {
        await apiClient.put(`/stores/${s.id}`, payload);
        toast.success("Store updated");
      } else {
        await apiClient.post(`/stores`, payload);
        toast.success("Store created");
      }
      setEditingStore(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const deleteStore = async (id) => {
    if (!window.confirm("Delete this store and all its events?")) return;
    try {
      await apiClient.delete(`/stores/${id}`);
      toast.success("Store deleted");
      loadData();
    } catch {
      toast.error("Could not delete");
    }
  };

  const saveEvent = async () => {
    const e = editingEvent;
    if (!e.store_id || !e.title || !e.event_date || !e.description) {
      return toast.error("Fill all required fields");
    }
    const payload = { ...e, capacity: parseInt(e.capacity) || 50 };
    try {
      if (e.id) {
        await apiClient.put(`/events/${e.id}`, payload);
        toast.success("Event updated");
      } else {
        await apiClient.post(`/events`, payload);
        toast.success("Event created");
      }
      setEditingEvent(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await apiClient.delete(`/events/${id}`);
      toast.success("Event deleted");
      loadData();
    } catch {
      toast.error("Could not delete");
    }
  };

  const viewRsvps = async (event) => {
    try {
      const res = await apiClient.get(`/events/${event.id}/rsvps`);
      setRsvpsView({ event, list: res.data });
    } catch {
      toast.error("Could not load RSVPs");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex flex-col bg-black text-white">
        <Header />
        <main className="flex-1 flex items-center justify-center px-6 py-16 relative overflow-hidden">
          <div className="zl-blob -top-40 -left-40 opacity-70" />
          <div className="zl-blob -bottom-40 -right-40 opacity-70" />
          <form
            onSubmit={handleLogin}
            data-testid="admin-login-form"
            className="relative w-full max-w-sm rounded-2xl border border-zinc-800 bg-zinc-950 p-8 zl-card-shadow"
          >
            <div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-5">
              <Lock className="w-6 h-6 text-[#FF5612]" />
            </div>
            <div className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#FF5612] mb-2">
              Restricted Area
            </div>
            <h1 className="font-display font-extrabold text-3xl tracking-tight mb-1 text-white">
              Admin Login
            </h1>
            <p className="text-sm text-zinc-400 mb-6">Manage stores, events, and RSVPs.</p>

            <label className="text-[10px] font-semibold tracking-[0.18em] uppercase text-zinc-500 mb-2 block">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              data-testid="admin-password-input"
              autoComplete="current-password"
              className="w-full rounded-xl bg-zinc-900 border border-zinc-800 focus:border-[#FF5612] py-3 px-4 outline-none text-white placeholder:text-zinc-600 mb-5 transition-colors"
              autoFocus
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="w-full bg-[#FF5612] hover:bg-[#FF6B2B] text-white py-3.5 rounded-xl font-bold text-sm disabled:opacity-60 transition-colors"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
            <div className="mt-5 rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3">
              <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-zinc-500 mb-1">
                Demo Password
              </div>
              <code className="text-sm font-mono text-[#FF5612]">zebra2026</code>
            </div>
          </form>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <Header />
      <main className="flex-1 px-4 sm:px-6 lg:px-10 py-8 md:py-12">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#FF5612] mb-2">
              Admin Console
            </div>
            <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight text-white">
              Manage Locations & Events
            </h1>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-white px-4 py-2 text-xs font-semibold transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>

        <div
          className="inline-flex p-1 rounded-full bg-zinc-900 border border-zinc-800 mb-8"
          data-testid="admin-tabs"
        >
          <button
            onClick={() => setTab("stores")}
            data-testid="admin-tab-stores"
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors inline-flex items-center gap-2 ${
              tab === "stores" ? "bg-[#FF5612] text-white" : "text-zinc-400 hover:text-white"
            }`}
          >
            <Store className="w-4 h-4" />
            Stores ({stores.length})
          </button>
          <button
            onClick={() => setTab("events")}
            data-testid="admin-tab-events"
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors inline-flex items-center gap-2 ${
              tab === "events" ? "bg-[#FF5612] text-white" : "text-zinc-400 hover:text-white"
            }`}
          >
            <Calendar className="w-4 h-4" />
            Events ({events.length})
          </button>
        </div>

        {tab === "stores" && (
          <section>
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setEditingStore({ ...emptyStore })}
                data-testid="add-store-btn"
                className="inline-flex items-center gap-2 bg-[#FF5612] hover:bg-[#FF6B2B] text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-colors"
              >
                <Plus className="w-4 h-4" /> New Store
              </button>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-zinc-800">
              <table className="w-full text-sm" data-testid="admin-stores-table">
                <thead className="bg-zinc-900">
                  <tr>
                    <th className="text-left p-3 text-xs uppercase tracking-[0.18em] text-zinc-400 font-semibold">Name</th>
                    <th className="text-left p-3 text-xs uppercase tracking-[0.18em] text-zinc-400 font-semibold">City</th>
                    <th className="text-left p-3 text-xs uppercase tracking-[0.18em] text-zinc-400 font-semibold">Phone</th>
                    <th className="text-left p-3 text-xs uppercase tracking-[0.18em] text-zinc-400 font-semibold">Coords</th>
                    <th className="text-right p-3 text-xs uppercase tracking-[0.18em] text-zinc-400 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-zinc-950">
                  {stores.map((s) => (
                    <tr key={s.id} className="border-t border-zinc-900 hover:bg-zinc-900/60">
                      <td className="p-3 font-semibold text-white">{s.name}</td>
                      <td className="p-3 text-zinc-400">{s.city}</td>
                      <td className="p-3 text-xs text-zinc-300">{s.phone}</td>
                      <td className="p-3 text-xs text-zinc-500">
                        {s.latitude.toFixed(3)}, {s.longitude.toFixed(3)}
                      </td>
                      <td className="p-3 text-right space-x-2 whitespace-nowrap">
                        <button
                          onClick={() => setEditingStore({ ...s })}
                          data-testid={`edit-store-${s.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-zinc-800 hover:bg-zinc-700 text-white transition-colors"
                        >
                          <Edit className="w-3 h-3" /> Edit
                        </button>
                        <button
                          onClick={() => deleteStore(s.id)}
                          data-testid={`delete-store-${s.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-zinc-800 hover:bg-[#FF5612] text-[#FF5612] hover:text-white transition-colors"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {stores.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-10 text-center text-zinc-500">
                        No stores yet. Click "New Store" to add one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "events" && (
          <section>
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setEditingEvent({ ...emptyEvent })}
                data-testid="add-event-btn"
                className="inline-flex items-center gap-2 bg-[#FF5612] hover:bg-[#FF6B2B] text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-colors"
              >
                <Plus className="w-4 h-4" /> New Event
              </button>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-zinc-800">
              <table className="w-full text-sm" data-testid="admin-events-table">
                <thead className="bg-zinc-900">
                  <tr>
                    <th className="text-left p-3 text-xs uppercase tracking-[0.18em] text-zinc-400 font-semibold">Title</th>
                    <th className="text-left p-3 text-xs uppercase tracking-[0.18em] text-zinc-400 font-semibold">Store</th>
                    <th className="text-left p-3 text-xs uppercase tracking-[0.18em] text-zinc-400 font-semibold">Date</th>
                    <th className="text-right p-3 text-xs uppercase tracking-[0.18em] text-zinc-400 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-zinc-950">
                  {events.map((ev) => (
                    <tr key={ev.id} className="border-t border-zinc-900 hover:bg-zinc-900/60">
                      <td className="p-3 font-semibold text-white">{ev.title}</td>
                      <td className="p-3 text-zinc-400">{ev.store_name}</td>
                      <td className="p-3 text-xs text-zinc-300">
                        {new Date(ev.event_date).toLocaleString()}
                      </td>
                      <td className="p-3 text-right space-x-2 whitespace-nowrap">
                        <button
                          onClick={() => viewRsvps(ev)}
                          data-testid={`view-rsvps-${ev.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-zinc-800 hover:bg-[#FF5612] text-white transition-colors"
                        >
                          <Users className="w-3 h-3" /> RSVPs
                        </button>
                        <button
                          onClick={() => setEditingEvent({ ...ev })}
                          data-testid={`edit-event-${ev.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-zinc-800 hover:bg-zinc-700 text-white transition-colors"
                        >
                          <Edit className="w-3 h-3" /> Edit
                        </button>
                        <button
                          onClick={() => deleteEvent(ev.id)}
                          data-testid={`delete-event-${ev.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-zinc-800 hover:bg-[#FF5612] text-[#FF5612] hover:text-white transition-colors"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {events.length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-10 text-center text-zinc-500">
                        No events yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>

      {editingStore && (
        <Modal title={editingStore.id ? "Edit Store" : "New Store"} onClose={() => setEditingStore(null)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Store Name *" value={editingStore.name} onChange={(v) => setEditingStore({ ...editingStore, name: v })} testid="store-name-input" />
            <FormField label="City *" value={editingStore.city} onChange={(v) => setEditingStore({ ...editingStore, city: v })} testid="store-city-input" />
            <FormField label="Address *" value={editingStore.address} onChange={(v) => setEditingStore({ ...editingStore, address: v })} className="md:col-span-2" testid="store-address-input" />
            <FormField label="Phone *" value={editingStore.phone} onChange={(v) => setEditingStore({ ...editingStore, phone: v })} testid="store-phone-input" />
            <FormField label="Hours" value={editingStore.hours} onChange={(v) => setEditingStore({ ...editingStore, hours: v })} testid="store-hours-input" />
            <FormField label="Latitude *" value={editingStore.latitude} onChange={(v) => setEditingStore({ ...editingStore, latitude: v })} type="number" testid="store-lat-input" />
            <FormField label="Longitude *" value={editingStore.longitude} onChange={(v) => setEditingStore({ ...editingStore, longitude: v })} type="number" testid="store-lng-input" />
            <FormField label="Image URL" value={editingStore.image_url} onChange={(v) => setEditingStore({ ...editingStore, image_url: v })} className="md:col-span-2" testid="store-image-input" />
          </div>
          <div className="mt-6 flex gap-3 justify-end">
            <button onClick={() => setEditingStore(null)} className="rounded-full border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-white px-5 py-2.5 text-sm font-semibold transition-colors">Cancel</button>
            <button onClick={saveStore} data-testid="save-store-btn" className="rounded-full bg-[#FF5612] hover:bg-[#FF6B2B] text-white px-5 py-2.5 text-sm font-semibold transition-colors">Save Store</button>
          </div>
        </Modal>
      )}

      {editingEvent && (
        <Modal title={editingEvent.id ? "Edit Event" : "New Event"} onClose={() => setEditingEvent(null)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Title *" value={editingEvent.title} onChange={(v) => setEditingEvent({ ...editingEvent, title: v })} className="md:col-span-2" testid="event-title-input" />
            <div className="md:col-span-2">
              <label className="text-[10px] font-semibold tracking-[0.18em] uppercase text-zinc-500 mb-2 block">
                Store *
              </label>
              <select
                value={editingEvent.store_id}
                onChange={(e) => setEditingEvent({ ...editingEvent, store_id: e.target.value })}
                data-testid="event-store-select"
                className="w-full rounded-xl bg-zinc-900 border border-zinc-800 focus:border-[#FF5612] py-2.5 px-3 outline-none text-white transition-colors"
              >
                <option value="">Select a store</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} - {s.city}</option>
                ))}
              </select>
            </div>
            <FormField label="Event Date & Time *" value={editingEvent.event_date?.slice(0, 16)} onChange={(v) => setEditingEvent({ ...editingEvent, event_date: v })} type="datetime-local" testid="event-date-input" />
            <FormField label="Capacity" value={editingEvent.capacity} onChange={(v) => setEditingEvent({ ...editingEvent, capacity: v })} type="number" testid="event-capacity-input" />
            <FormField label="Image URL" value={editingEvent.image_url} onChange={(v) => setEditingEvent({ ...editingEvent, image_url: v })} className="md:col-span-2" testid="event-image-input" />
            <div className="md:col-span-2">
              <label className="text-[10px] font-semibold tracking-[0.18em] uppercase text-zinc-500 mb-2 block">
                Description *
              </label>
              <textarea
                value={editingEvent.description}
                onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })}
                rows={4}
                data-testid="event-description-input"
                className="w-full rounded-xl bg-zinc-900 border border-zinc-800 focus:border-[#FF5612] p-3 outline-none text-white text-sm transition-colors"
              />
            </div>
          </div>
          <div className="mt-6 flex gap-3 justify-end">
            <button onClick={() => setEditingEvent(null)} className="rounded-full border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-white px-5 py-2.5 text-sm font-semibold transition-colors">Cancel</button>
            <button onClick={saveEvent} data-testid="save-event-btn" className="rounded-full bg-[#FF5612] hover:bg-[#FF6B2B] text-white px-5 py-2.5 text-sm font-semibold transition-colors">Save Event</button>
          </div>
        </Modal>
      )}

      {rsvpsView && (
        <Modal title={`RSVPs · ${rsvpsView.event.title}`} onClose={() => setRsvpsView(null)}>
          {rsvpsView.list.length === 0 ? (
            <p className="text-sm text-zinc-400 py-6 text-center">No RSVPs yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-zinc-800">
              <table className="w-full text-sm" data-testid="rsvps-table">
                <thead className="bg-zinc-900">
                  <tr>
                    <th className="text-left p-3 text-xs uppercase tracking-[0.18em] text-zinc-400 font-semibold">Name</th>
                    <th className="text-left p-3 text-xs uppercase tracking-[0.18em] text-zinc-400 font-semibold">Mobile</th>
                    <th className="text-left p-3 text-xs uppercase tracking-[0.18em] text-zinc-400 font-semibold">When</th>
                  </tr>
                </thead>
                <tbody className="bg-zinc-950">
                  {rsvpsView.list.map((r) => (
                    <tr key={r.id} className="border-t border-zinc-900">
                      <td className="p-3 font-semibold text-white">{r.name}</td>
                      <td className="p-3 text-zinc-300">{r.mobile}</td>
                      <td className="p-3 text-zinc-500 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-5 text-right">
            <button onClick={() => setRsvpsView(null)} className="rounded-full border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-white px-5 py-2.5 text-sm font-semibold transition-colors">Close</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
    <div
      className="bg-zinc-950 border border-zinc-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto zl-card-shadow"
      onClick={(e) => e.stopPropagation()}
      data-testid="admin-modal"
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 sticky top-0 bg-zinc-950 z-10">
        <h3 className="font-display font-bold text-lg tracking-tight text-white">{title}</h3>
        <button onClick={onClose} className="text-zinc-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const FormField = ({ label, value, onChange, type = "text", className = "", testid }) => (
  <div className={className}>
    <label className="text-[10px] font-semibold tracking-[0.18em] uppercase text-zinc-500 mb-2 block">
      {label}
    </label>
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full rounded-xl bg-zinc-900 border border-zinc-800 focus:border-[#FF5612] py-2.5 px-3 outline-none text-white transition-colors"
    />
  </div>
);

export default AdminPage;
